import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { ArrowLeft, Activity, CheckCircle, XCircle, Timer, BarChart3 } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface DailyCount {
  date: string;
  count: number;
}

interface FailingStep {
  chainStepId: string;
  failCount: number;
}

interface WorkflowStats {
  workflowId: string;
  totalExecutions: number;
  succeededExecutions: number;
  failedExecutions: number;
  successRate: number;
  avgDurationMs: number;
  executionsPerDay: DailyCount[];
  topFailingSteps: FailingStep[];
  lookbackDays: number;
}

interface AbTest {
  id: string;
  workflowId: string;
  variantAVersionId: string;
  variantBVersionId: string;
  trafficSplit: number;
  successMetricKey: string;
  startedAt: string;
  concludedAt: string | null;
  winner: string | null;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function WorkflowAnalyticsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stats, setStats] = useState<WorkflowStats | null>(null);
  const [abTests, setAbTests] = useState<AbTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'ab-tests'>('overview');

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [statsRes, abRes] = await Promise.all([
        apiClient.get<WorkflowStats>(`/workflows/${id}/analytics`),
        apiClient.get<AbTest[]>(`/workflows/${id}/ab-tests`),
      ]);
      setStats(statsRes.data);
      setAbTests(abRes.data);
    } catch {
      flash('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function concludeTest(testId: string, winner: string) {
    try {
      await apiClient.post(`/workflows/${id}/ab-tests/${testId}/conclude`, { winner });
      flash(t('ab_test.concluded') ?? 'Test concluded');
      load();
    } catch {
      flash('Failed to conclude test');
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading analytics...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-muted-foreground">{t('flow_analytics.no_data')}</p>
      </div>
    );
  }

  const maxDayCount = Math.max(...stats.executionsPerDay.map((d) => d.count), 1);

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/chains')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">{t('flow_analytics.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('flow_analytics.lookback', { days: stats.lookbackDays })}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {(['overview', 'ab-tests'] as const).map((tb) => (
          <button
            key={tb}
            onClick={() => setTab(tb)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              tab === tb
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tb === 'overview' ? t('flow_analytics.title') : t('ab_test.title')}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === 'overview' && (
        <div className="space-y-6">
          {/* KPI cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              icon={<Activity className="h-5 w-5 text-primary" />}
              label={t('flow_analytics.total_executions')}
              value={stats.totalExecutions.toString()}
            />
            <KpiCard
              icon={<CheckCircle className="h-5 w-5 text-emerald-500" />}
              label={t('flow_analytics.success_rate')}
              value={`${Math.round(stats.successRate * PERCENT)}%`}
            />
            <KpiCard
              icon={<XCircle className="h-5 w-5 text-destructive" />}
              label="Failed"
              value={stats.failedExecutions.toString()}
            />
            <KpiCard
              icon={<Timer className="h-5 w-5 text-amber-500" />}
              label={t('flow_analytics.avg_duration')}
              value={
                stats.avgDurationMs > 0
                  ? `${(stats.avgDurationMs / MS_PER_SEC).toFixed(1)}s`
                  : '—'
              }
            />
          </div>

          {/* Executions per day mini-bar chart */}
          <div className="rounded-xl border bg-card p-5">
            <div className="mb-4 flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">{t('flow_analytics.executions_per_day')}</h3>
            </div>
            {stats.executionsPerDay.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('flow_analytics.no_data')}</p>
            ) : (
              <div className="flex h-32 items-end gap-1">
                {stats.executionsPerDay.map((d) => (
                  <div
                    key={d.date}
                    className="group relative flex flex-1 flex-col items-center"
                  >
                    <div
                      className="w-full rounded-t bg-primary/70 transition-all group-hover:bg-primary"
                      style={{
                        height: `${Math.max(MIN_BAR_HEIGHT, (d.count / maxDayCount) * BAR_MAX_HEIGHT)}%`,
                      }}
                    />
                    <div className="absolute -top-5 hidden rounded bg-black/80 px-1.5 py-0.5 text-[10px] text-white group-hover:block">
                      {d.date}: {d.count}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top failing steps */}
          {stats.topFailingSteps.length > 0 && (
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-3 text-sm font-semibold">{t('flow_analytics.top_failing_steps')}</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="py-1 text-left font-medium">{t('flow_analytics.step_id')}</th>
                    <th className="py-1 text-right font-medium">{t('flow_analytics.fail_count')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topFailingSteps.map((s) => (
                    <tr key={s.chainStepId} className="border-b last:border-0">
                      <td className="py-2 font-mono text-xs">{s.chainStepId}</td>
                      <td className="py-2 text-right text-destructive">{s.failCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* A/B Tests tab */}
      {tab === 'ab-tests' && (
        <div className="space-y-4">
          {abTests.length === 0 ? (
            <div className="rounded-xl border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
              {t('ab_test.empty')}
            </div>
          ) : (
            abTests.map((test) => (
              <div key={test.id} className="rounded-xl border bg-card p-5">
                <div className="flex items-start justify-between">
                  <div className="space-y-1 text-sm">
                    <p>
                      <span className="font-medium">{t('ab_test.variant_a')}</span>{' '}
                      <span className="font-mono text-xs">{test.variantAVersionId.slice(0, ABBREV_LEN)}</span>
                      {' vs '}
                      <span className="font-medium">{t('ab_test.variant_b')}</span>{' '}
                      <span className="font-mono text-xs">{test.variantBVersionId.slice(0, ABBREV_LEN)}</span>
                    </p>
                    <p className="text-muted-foreground">
                      {t('ab_test.traffic_split')}: {test.trafficSplit}% / {PERCENT - test.trafficSplit}%
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {t('ab_test.started')}: {new Date(test.startedAt).toLocaleDateString()}
                    </p>
                    {test.winner && (
                      <span className="inline-block rounded-full bg-emerald-900/40 px-2 py-0.5 text-xs font-medium text-emerald-200">
                        {t('ab_test.winner')}: {test.winner}
                      </span>
                    )}
                  </div>
                  {!test.concludedAt && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => concludeTest(test.id, 'A')}>
                        A {t('ab_test.winner')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => concludeTest(test.id, 'B')}>
                        B {t('ab_test.winner')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => concludeTest(test.id, 'INCONCLUSIVE')}>
                        {t('ab_test.inconclusive')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold">{value}</p>
    </div>
  );
}

const PERCENT = 100;
const MS_PER_SEC = 1000;
const MIN_BAR_HEIGHT = 2;
const BAR_MAX_HEIGHT = 100;
const ABBREV_LEN = 8;
